import React from 'react';
import './NavigationBar.scss';
import Logo from '../Logo/Logo';
import NavItemList from '../NavItemList/NavItemList';
import Search from '../Search/Search';
import ProfileButton from '../ProfileButton/ProfileButton';
import BurgerMenu from '../BurgerMenu/BurgerMenu';
import { FlattenedMenuItem } from '../../../utils/menuFlattener';
import { HeaderMode } from '../../../contexts/MinisiteLayoutContext';
import { buildLoginUrl } from '../../../utils/authHelper';

interface NavigationBarProps {
  mode?: HeaderMode;
  navItems?: FlattenedMenuItem[];
  watchNavItems?: FlattenedMenuItem[];
  cust_type?: string;
  username?: string;
  currentLang?: 'en' | 'zh';
  onLoginClick?: () => void;
  onBurgerMenuClick?: () => void;
  isSearchOpen?: boolean;
  onSearchClick?: () => void;
  isMobileMenuOpen?: boolean;
  loginUrl?: string;
  isProfileLoading?: boolean;
  logoUrl?: string;
}

const NavigationBar: React.FC<NavigationBarProps> = ({ 
  mode = 'general',
  navItems = [],
  watchNavItems = [],
  cust_type = 'not-logged-in',
  username,
  currentLang = 'en',
  onLoginClick,
  onBurgerMenuClick,
  isSearchOpen = false,
  onSearchClick,
  isMobileMenuOpen = false,
  loginUrl = '#',
  isProfileLoading = false,
  logoUrl=''
}) => {
  // Convert FlattenedMenuItem to NavItem format
  const convertedNavItems = navItems.map(item => ({
    href: (item.forceLogin && !username) ? buildLoginUrl(currentLang, item.url) : item.url,
    label: item.label,
    dropdownItems: item.children?.map(child => ({
      href: (child.forceLogin && !username) ? buildLoginUrl(currentLang, child.url || '#') : (child.url || '#'),
      label: child.label,
      target: child.target || '_self',
      displayFor: child.displayFor
    })),
    className: '',
    buttonClass: '',
    displayFor: item.displayFor
  }));

  // Language switcher label
  const langText = currentLang === 'zh' ? 'ENG' : '中文';
  const isLoggedIn = Boolean(cust_type && cust_type !== 'not-logged-in');

  // Watch menu items - try to get from API first, then fallback
  const watchItems = watchNavItems.length > 0 ? watchNavItems.map(item => ({
    href: (item.forceLogin && !username) ? buildLoginUrl(currentLang, item.url) : item.url,
    label: item.label,
    buttonClass: 'watch-button',
    dropdownItems: item.children?.map(child => ({
      href: (child.forceLogin && !username) ? buildLoginUrl(currentLang, child.url || '#') : (child.url || '#'),
      label: child.label,
      target: child.target || '_self',
      displayFor: child.displayFor
    })),
    displayFor: item.displayFor
  })) : [{
    href: `https://nowplayer.now.com/?lang=${currentLang === 'zh' ? 'zh' : 'en'}`,
    label: currentLang === 'zh' ? '觀看' : 'Watch',
    buttonClass: 'watch-button',
    dropdownItems: [
      { 
        href: `https://nowplayer.now.com/tvguide?lang=${currentLang === 'zh' ? 'zh' : 'en'}`, 
        label: currentLang === 'zh' ? '電視指南' : 'TV Guide', 
        target: '_blank' 
      },
      { 
        href: `https://nowplayer.now.com/?lang=${currentLang === 'zh' ? 'zh' : 'en'}`, 
        label: currentLang === 'zh' ? 'Now Player 主頁' : 'Now Player Home', 
        target: '_blank' 
      },
      { 
        href: `https://nowplayer.now.com/ondemand?lang=${currentLang === 'zh' ? 'zh' : 'en'}`, 
        label: currentLang === 'zh' ? '隨選' : 'On Demand', 
        target: '_blank' 
      }
    ],
    displayFor: ['all']
  }];

  const handleLanguageChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    
    const lang = e.currentTarget.getAttribute('data-value') || '';
    const main_link = window.location.href.split('?');
    let main_url;
    
    if (main_link.length >= 2) {
      const prefix = encodeURIComponent('lang') + '=';
      const pars = main_link[1].split(/[&;]/g);
      
      // Reverse iteration as may be destructive
      for (let i = pars.length; i-- > 0;) {
        // Idiom for string.startsWith
        if (pars[i].lastIndexOf(prefix, 0) !== -1) {
          pars.splice(i, 1);
        }
      }
      main_url = main_link[0] + '?' + pars.join('&');
    } else {
      main_url = window.location.href;
    }
    
    // Set cookie with 20 years expiry
    const d = new Date();
    d.setTime(d.getTime() + (20 * 365 * 24 * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    document.cookie = "lang=" + lang + ";" + expires + ";SameSite=None;Secure;domain=.nowtv.now.com;path=/";
    
    window.history.replaceState(null, '', main_url);
    window.location.reload();
  };

  return (
    <header className={`navigation lang-${currentLang} ${mode === 'logo-language' ? 'logo-language-header' : ''} ${mode === 'logo-lang-enquiry' ? 'logo-lang-enquiry-header' : ''}`}>
      <nav className="navbar navbar-expand-lg">
        <Logo href="/" logoUrl={logoUrl} />
        
        {mode !== 'logo-language' && (
          <>
            {/* Mobile search button */}
            {/* mode !== 'logo-lang-enquiry' && (
              <Search 
                onClick={onSearchClick} 
                className="mobile-search d-lg-none" 
                isOpen={isSearchOpen}
              />
            ) */}
            
            {/* Burger menu */}
            <BurgerMenu 
              onClick={onBurgerMenuClick} 
              isOpen={isMobileMenuOpen}
            />
          </>
        )}
        
        {/* Desktop navigation */}
        <div className="collapse navbar-collapse nav-center">
          {mode !== 'logo-language' && mode !== 'logo-lang-enquiry' && (
            <NavItemList 
              items={convertedNavItems} 
              cust_type={cust_type}
            />
          )}
          
          <div className="nav-right">
            {/* mode !== 'logo-language' && mode !== 'logo-lang-enquiry' && (
              <Search 
                onClick={onSearchClick} 
                className="desktop-search" 
                isOpen={isSearchOpen}
              />
            ) */}
            
            {/* Language switcher */}
            {(mode === 'general' || mode === 'logo-language' || mode === 'logo-lang-enquiry') && (
              <button 
                className="lang-button" 
                onClick={handleLanguageChange}
                type="button"
                data-value={currentLang === 'zh' ? "op/UwDjU7jkgMlWyDIDgUg==" : "mcHbFvVz6GasaoYXRD6O4Q=="}
              >
                {langText}
              </button>
            )}
            
            {mode !== 'logo-language' && (
              <>
                {/* Watch button */}
                <NavItemList 
                  items={watchItems} 
                  className="watch-menu"
                  cust_type={cust_type}
                />
                
                {/* Profile/Login button */}
                <ProfileButton
                  status={isLoggedIn ? 'logged-in' : 'login'}
                  username={username}
                  href={isLoggedIn ? '/my-account' : loginUrl}
                  onClick={onLoginClick}
                  currentLang={currentLang}
                  isLoading={isProfileLoading}
                />
              </>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default NavigationBar;
